/**
 * Shared role definitions for user/site access control.
 * Roles are per-site; the same user may hold different roles at different sites.
 * An additional platform-level `admin` role still gates the Users & Roles admin UI,
 * but is derived from `User.role === "admin"` on the session.
 */

export const ROLE_VALUES = ["admin", "manager", "inspector", "viewer"] as const;

export type Role = (typeof ROLE_VALUES)[number];

export interface RoleMeta {
  value: Role;
  label: string;
  description: string;
  /** Hex color used for badge foreground/border. */
  color: string;
  /** rgba fill for the badge background. */
  bg: string;
  /** rgba border for the badge. */
  border: string;
}

export const ROLE_META: Record<Role, RoleMeta> = {
  admin: {
    value: "admin",
    label: "Admin",
    description: "Full access across every asset and company",
    color: "var(--cte-orange)",
    bg: "color-mix(in oklab, var(--cte-orange) 12%, transparent)",
    border: "color-mix(in oklab, var(--cte-orange) 40%, transparent)",
  },
  manager: {
    value: "manager",
    label: "Manager",
    description: "Full access on assigned assets — invite & manage their team",
    color: "rgb(37, 99, 235)",
    bg: "rgba(37, 99, 235, 0.07)",
    border: "rgba(37, 99, 235, 0.25)",
  },
  inspector: {
    value: "inspector",
    label: "Inspector",
    description: "Manage equipment, inspections & defects on assigned assets",
    color: "rgb(5, 150, 105)",
    bg: "rgba(5, 150, 105, 0.07)",
    border: "rgba(5, 150, 105, 0.25)",
  },
  viewer: {
    value: "viewer",
    label: "Viewer",
    description: "Read-only access to assigned assets",
    color: "rgb(100, 116, 139)",
    bg: "rgba(100, 116, 139, 0.07)",
    border: "rgba(100, 116, 139, 0.25)",
  },
};

export const ROLE_LIST: RoleMeta[] = ROLE_VALUES.map((v) => ROLE_META[v]);

export function isRole(value: unknown): value is Role {
  return (
    typeof value === "string" &&
    (ROLE_VALUES as readonly string[]).includes(value)
  );
}

export function toRole(value: unknown, fallback: Role = "viewer"): Role {
  return isRole(value) ? value : fallback;
}
